/**
 * Utility functions for handling identity-related features
 */

// Standard gender identity options
export const genderIdentityOptions = [
  'woman',
  'man',
  'trans woman',
  'trans man',
  'non-binary',
  'genderfluid',
  'agender',
  'questioning',
  'custom'
];

// Standard sexual orientation options
export const sexualOrientationOptions = [
  'lesbian',
  'gay',
  'bisexual',
  'pansexual',
  'asexual',
  'queer',
  'questioning',
  'custom'
];

// Standard pronoun options
export const pronounOptions = [
  'She/her',
  'He/him',
  'They/them',
  'Ze/zir',
  'She/they',
  'He/they',
  'Custom'
];

// Visibility options
export const visibilityOptions = [
  { value: 'public', label: 'Public (Everyone can see)' },
  { value: 'community', label: 'Community (Only Outcaribbean members)' },
  { value: 'private', label: 'Private (Only you can see)' }
];

/**
 * Determines if a user's identity marker should be displayed based on visibility settings
 * @param {Object} user - The user object
 * @param {string} field - The identity field to check (e.g., 'genderIdentity')
 * @param {Object} viewerUser - The user viewing the profile (null if not logged in)
 * @returns {boolean} - Whether the field should be displayed
 */
export const shouldDisplayIdentity = (user, field, viewerUser) => {
  // If no visibility settings or field doesn't exist, don't display
  if (!user || !user.visibilitySettings || !user.visibilitySettings[field]) {
    return false;
  }

  const visibility = user.visibilitySettings[field];

  // Public is always visible
  if (visibility === 'public') {
    return true;
  }

  // Community is visible only to logged-in users
  if (visibility === 'community' && viewerUser) {
    return true;
  }

  // Private is visible only to the user themselves
  if (visibility === 'private' && viewerUser && viewerUser.id === user.id) {
    return true;
  }

  return false;
};

/**
 * Gets the display value for an identity field, respecting visibility settings
 * @param {Object} user - The user object
 * @param {string} field - The identity field to get (e.g., 'genderIdentity')
 * @param {Object} viewerUser - The user viewing the profile (null if not logged in)
 * @returns {string|Array|null} - The field value or null if not visible
 */
export const getVisibleIdentity = (user, field, viewerUser) => {
  if (shouldDisplayIdentity(user, field, viewerUser)) {
    return user[field];
  }
  return null;
};

/**
 * Formats identity tags for display
 * @param {Array|string} identityValue - The identity value to format
 * @returns {Array} - Array of formatted identity tags
 */
export const formatIdentityTags = (identityValue) => {
  if (!identityValue) return [];
  
  // If it's a string, wrap in array
  if (typeof identityValue === 'string') {
    return [identityValue];
  }
  
  // If it's already an array, return it
  if (Array.isArray(identityValue)) {
    return identityValue;
  }
  
  return [];
};

/**
 * Checks if a user is safe for a specific identity group
 * @param {Object} user - The user to check
 * @param {string} identityGroup - The identity group to check for (e.g., 'trans women')
 * @returns {boolean} - Whether the user is likely safe for the identity group
 */
export const isSafeForIdentity = (user, identityGroup) => {
  // This is a simplified implementation
  // In a real app, this would use more sophisticated logic based on:
  // - User ratings from people with that identity
  // - Explicit "safe for X" tags
  // - Reports or flags
  
  // For now, we'll use a simple tag-based approach
  if (!user.travelStyle) return false;
  
  const safetyTags = {
    'trans women': ['trans-friendly', 'lgbtq-friendly'],
    'trans men': ['trans-friendly', 'lgbtq-friendly'],
    'non-binary': ['non-binary-inclusive', 'lgbtq-friendly'],
    'lesbian': ['lesbian-friendly', 'women-friendly', 'lgbtq-friendly'],
    'gay men': ['gay-friendly', 'lgbtq-friendly']
  };
  
  const relevantTags = safetyTags[identityGroup] || [];
  
  return relevantTags.some(tag => user.travelStyle.includes(tag));
};

/**
 * Generates a display name based on anonymity settings
 * @param {Object} user - The user object
 * @returns {string} - The display name to show
 */
export const getDisplayName = (user) => {
  if (!user) return '';
  
  if (user.anonymousMode) {
    // In anonymous mode, use first initial + location
    const firstInitial = user.name.charAt(0);
    const location = user.location.split(',')[0]; // Just the city
    return `${firstInitial} from ${location}`;
  }
  
  return user.name;
};

/**
 * Gets the avatar to display based on anonymity settings
 * @param {Object} user - The user object
 * @returns {string} - URL to the avatar image
 */
export const getDisplayAvatar = (user) => {
  if (!user) return '';
  
  if (user.anonymousMode) {
    // In anonymous mode, use a generic avatar
    // In a real app, this would be a consistent but anonymous avatar
    return 'https://cdn.pixabay.com/photo/2015/10/05/22/37/blank-profile-picture-973460_1280.png';
  }
  
  return user.avatar;
};
